import { Button, Fieldset, Group, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { MAX_OUTCOMES, MIN_OUTCOMES, type OutcomeDraft } from '../interaction/draft';
import { MoveButtons } from './MoveButtons';

/**
 * The results a personality quiz can land a reader on.
 *
 * It sits above the questions on purpose. An answer points at an outcome, so the outcomes have to
 * exist and have names before pointing at one means anything, and an editor who meets the outcomes
 * first writes questions that lead somewhere.
 */
export function OutcomesEditor({
  outcomes,
  onUpdate,
  onAdd,
  onRemove,
  onMove,
}: {
  outcomes: readonly OutcomeDraft[];
  onUpdate: (key: string, patch: Partial<OutcomeDraft>) => void;
  onAdd: () => void;
  onRemove: (key: string) => void;
  onMove: (index: number, delta: number) => void;
}) {
  return (
    <section aria-labelledby="outcomes-heading">
      <Text component="h3" id="outcomes-heading" fw={500} size="sm">
        Outcomes
      </Text>
      <Text c="dimmed" size="xs" mb="xs">
        The results readers can land on. Between {MIN_OUTCOMES} and {MAX_OUTCOMES} of them.
      </Text>

      <Stack gap="sm">
        {outcomes.map((outcome, index) => (
          <Fieldset
            key={outcome.key}
            legend={`Outcome ${String(index + 1)}`}
            radius="md"
            variant="filled"
          >
            <Stack gap="xs">
              <TextInput
                id={`outcome-title-${outcome.key}`}
                label="Name"
                placeholder="The Traditionalist"
                value={outcome.title}
                maxLength={200}
                onChange={(event) => onUpdate(outcome.key, { title: event.target.value })}
              />
              <Textarea
                id={`outcome-description-${outcome.key}`}
                label="Description"
                description="What the reader reads when they land here."
                autosize
                minRows={2}
                maxRows={6}
                value={outcome.description}
                maxLength={2000}
                onChange={(event) => onUpdate(outcome.key, { description: event.target.value })}
              />
              <TextInput
                id={`outcome-image-${outcome.key}`}
                label="Image address"
                description="Optional. A full web address starting with https."
                placeholder="https://"
                value={outcome.imageUrl}
                maxLength={2048}
                onChange={(event) => onUpdate(outcome.key, { imageUrl: event.target.value })}
              />
              <Group gap="xs" mt={4}>
                <MoveButtons
                  what={`outcome ${String(index + 1)}`}
                  index={index}
                  total={outcomes.length}
                  onMove={onMove}
                />
                <Button
                  variant="subtle"
                  color="gray"
                  size="compact-sm"
                  onClick={() => onRemove(outcome.key)}
                  disabled={outcomes.length <= MIN_OUTCOMES}
                  aria-label={`Remove outcome ${String(index + 1)}`}
                >
                  Remove
                </Button>
              </Group>
            </Stack>
          </Fieldset>
        ))}
      </Stack>

      <Button
        variant="default"
        size="compact-sm"
        mt="sm"
        onClick={onAdd}
        disabled={outcomes.length >= MAX_OUTCOMES}
      >
        Add outcome
      </Button>
    </section>
  );
}
